import { useEffect, useRef, useState } from 'react';
import { GALLERY_URL, useTemplateGallery } from '../lib/gallery.ts';
import Modal from '../components/Modal.tsx';
import { api } from '../lib/api.ts';
import { resolveModelChoice, type ChatModelOption } from '../lib/models.ts';
import type { SlideEntry } from '../../shared/deck.ts';
import { X, Check } from '../lib/icons.tsx';

/** The text-list fallback's entries, from GET /api/templates. */
type TemplateListEntry = { componentName: string; title?: string; pathTail?: string; description?: string };

// The workbench's gallery page, served LIVE through the preview pool — the same
// tiles the "Add a slide" chooser shows (AddSlideChooser.tsx), reused here to
// pick the TARGET template for a re-flow. warmGallery() (fired on editor mount)
// keeps the Vite child warm, so this modal opens onto a ready gallery.
//
// `mode=pick-one` is the change-template embed mode (see PICK_ONE in
// workbench/src/gallery/Gallery.jsx): the picked tile carries a tick, the
// variant picker is an overlay that STAYS OPEN on a pick (so the tick lands on
// the tile the user just clicked, never on a base tile back on the grid), and
// the surfaces this modal can't act on (the multi-select batch bar, the RKH
// library tab) are gone rather than silently inert.

/**
 * "Change template" chooser — opened from a slide's ⋮ menu. Unlike the add
 * chooser it never inserts: the user picks ONE base template (the visual
 * gallery, or the text-list fallback when the gallery child can't boot) and
 * confirms. The parent then fires a slide-scope AI turn that re-flows the
 * current slide's content into that template (a read_template + edit_slide chat
 * turn, surfaced as a normal preview → apply card).
 *
 * No free-text instructions box: it read as a "tell the AI more" field that
 * non-technical users couldn't judge (Jake via Alex, 2026-07-30). Anything
 * beyond "use this template" is a follow-up message in the chat, where the reply
 * is visible.
 *
 * CONFIRMING CLOSES THE MODAL AND THE WORK CARRIES ON (Jake, 2026-08-01). The
 * re-flow takes a minute or two, and this used to hold the user on a working
 * modal for all of it with the slide they had just changed hidden behind it. It
 * now behaves like every other request: the parent fires the turn, closes this,
 * and reports the outcome where the user is — on the slide (toasts, the Activity
 * tray, the filmstrip dot, the ProposalBanner). A background run is stopped from
 * the Activity tab, the one place that stops any run, so there is no Stop here.
 *
 * The only state this modal has beyond picking, then, is a PRE-FLIGHT refusal
 * (`run.phase === 'failed'` before anything started, e.g. the slide already has
 * a turn in flight) — shown as a note above the footer with the pick intact.
 *
 * CLOSING IS NEVER DESTRUCTIVE: ✕, Esc and backdrop-click only ever abandon a
 * pick.
 */
/**
 * The change-template turn, as Canvas watches it. Declared here because it is
 * this component's prop; Canvas imports it rather than restating it.
 *
 * The chooser only ever RENDERS the 'failed' arm — a pre-flight refusal, which
 * is the one outcome that happens while it is still open. It closes the moment
 * the turn is fired, so 'running' is a phase it never sees but the run really
 * has, and the type says the truth rather than what one reader needs.
 */
export type TemplateRun = {
  key: string;
  slideId: string;
  detail?: string;
  phase: 'starting' | 'running' | 'failed';
  message?: string;
  startedAt?: number;
};

/** A picked base template. `model` is NOT part of it — the modal attaches
 *  that at confirm from its own picker, which is why the selection state and
 *  the confirm payload are two types rather than one optional field. */
export type TemplatePick = { pathTail?: string; title?: string; variant?: string | null };

/** What the confirm hands back. */
export type TemplateChoice = TemplatePick & { model: string | null };

type ChangeTemplateChooserProps = {
  slide: SlideEntry;
  onConfirm: (choice: TemplateChoice) => void;
  onClose: () => void;
  busy?: boolean;
  /** A PRE-FLIGHT refusal only (`phase: 'failed'` before anything started) —
   *  once a turn is away this modal closes and reports by toast. */
  run?: TemplateRun | null;
  onOpenChat?: () => void;
  /** Empty hides the Model row. */
  modelOptions?: ChatModelOption[];
  /** The ⚙ "Default model": this re-flows ONE slide in place, so unlike
   *  merge/split it has no reason to reach past the user's own setting. */
  defaultModel?: string | null;
};

export default function ChangeTemplateChooser({
  slide,
  onConfirm,
  onClose,
  busy,
  run,
  onOpenChat,
  modelOptions,
  defaultModel,
}: ChangeTemplateChooserProps) {
  // The gallery's three-state loader — shared with AddSlideChooser.
  const {
    state: galleryState,
    painted: galleryPainted,
    onPainted,
    onFailed,
    templates,
  } = useTemplateGallery<TemplateListEntry>(() =>
    api.get<{ templates?: TemplateListEntry[] }>('/api/templates').then((d) => d.templates || []),
  );
  const [selected, setSelected] = useState<TemplatePick | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [model, setModel] = useState<string | null>(null);
  const modelChoice = model ?? resolveModelChoice(modelOptions, defaultModel);
  const locked = !!busy;
  const note = run?.phase === 'failed' ? run : null;

  // Picks arrive from the gallery iframe. Same-origin + same-frame only. Here a
  // pick only SELECTS the target (ticked on the tile by the gallery, named in the
  // footer here): nothing happens until the "Change template" button confirms.
  // In pick-one mode the gallery no longer offers batch or library picks, so the
  // bank guard below is belt-and-braces rather than a live filter.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === 'studio-gallery-close') {
        onClose(); // Esc inside the iframe closes; a live turn keeps running
        return;
      }
      if (locked) return;
      if (e.data?.type !== 'studio-gallery-pick') return;
      if (e.data.bank) return; // a library slide, not a base template — ignore
      setSelected({
        pathTail: e.data.pathTail,
        title: e.data.title,
        variant: e.data.variant || null,
      });
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onClose, locked]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const theme = document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';

  function confirm() {
    if (!selected || locked) return;
    onConfirm({
      pathTail: selected.pathTail,
      title: selected.title,
      variant: selected.variant,
      model: modelChoice,
    });
  }

  return (
    <Modal ariaLabel="Change template" onClose={onClose} className="modal--add-slide">
      <header className="modal__header modal__header--add-slide">
        <h3 className="modal__title modal__title--sm" id="change-tpl-title">
          Change template{slide?.title ? `: "${slide.title}"` : ''}
        </h3>
        <span className="add-slide__spacer" />
        <span className="add-slide__hint muted small">
          Pick a base template; the AI re-flows this slide's content into it.
        </span>
        <button type="button" className="iconbtn" onClick={onClose} title="Close" aria-label="Close">
          <X />
        </button>
      </header>

      {(galleryState === 'loading' || (galleryState === 'ready' && !galleryPainted)) && (
        <div className="add-slide__skeleton" aria-hidden="true">
          {Array.from({ length: 12 }, (_, i) => (
            <span key={i} className="add-slide__skeleton-tile" />
          ))}
        </div>
      )}
      {galleryState === 'ready' && (
        /* Same rule as AddSlideChooser: the HTML answering is not the gallery
             being ready to look at. Hold the skeleton until `load` fires. */
        <div className={'add-slide__gallery' + (galleryPainted ? '' : ' add-slide__gallery--hidden')}>
          <iframe
            ref={iframeRef}
            className="add-slide__gallery-frame"
            src={`${GALLERY_URL}?theme=${theme}&mode=pick-one`}
            title="Base templates"
            onLoad={onPainted}
            onError={onFailed}
          />
          {locked && <div className="add-slide__gallery-busy" aria-hidden="true" />}
        </div>
      )}
      {galleryState === 'unavailable' && (
        <div className="add-slide__list">
          {templates === null && <div className="muted small">Loading…</div>}
          {templates !== null && templates.length === 0 && <div className="muted small">No templates available.</div>}
          {(templates || []).map((t) => {
            const isSel = selected?.pathTail === (t.pathTail || t.componentName);
            return (
              <button
                key={t.componentName}
                type="button"
                className={'add-slide__tpl' + (isSel ? ' add-slide__tpl--active' : '')}
                aria-pressed={isSel}
                onClick={() => setSelected({ pathTail: t.pathTail || t.componentName, title: t.title, variant: null })}
                disabled={locked}
              >
                <span className="add-slide__tpl-title">{t.title}</span>
                {t.description && <span className="add-slide__tpl-desc">{t.description}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="change-tpl__footer">
        {/* A refused turn leaves the pick intact (it usually means "something
              else is already working on this slide"), so the note explains what
              happened and the ordinary footer below is the way forward. */}
        {note && (
          <div className="change-tpl__note change-tpl__note--error" role="status">
            <span className="change-tpl__note-text">{note.message}</span>
            {onOpenChat && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={onOpenChat}>
                Open the chat
              </button>
            )}
          </div>
        )}
        {/* Which model re-flows the slide. Absent this row the turn rode
              whatever the chat pill was last left on, which is a strange thing
              for a modal to decide for you. Defaults to the ⚙ setting; hidden
              when the server offers no choice. */}
        {(modelOptions || []).length > 0 && (
          <div className="change-tpl__foot-row change-tpl__foot-row--models">
            <span className="muted small">Model</span>
            <div className="conversation__chips">
              {(modelOptions || []).map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={'chip' + (modelChoice === m.value ? ' chip--active' : '')}
                  onClick={() => setModel(m.value)}
                  aria-pressed={modelChoice === m.value}
                  title={m.hint}
                  disabled={locked}
                >
                  {m.label}
                  {m.detail ? ` · ${m.detail}` : ''}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="change-tpl__foot-row">
          {/* Full strength once something is picked, not muted grey: three
                rounds of feedback have been some form of "it's not clear
                anything is selected". role=status so the change is announced
                as well as shown. */}
          <span
            className={'change-tpl__selected' + (selected ? ' change-tpl__selected--on' : ' muted small')}
            role="status"
          >
            {selected ? (
              <>
                <Check className="change-tpl__tick" />
                <strong>{selected.title}</strong>
                {selected.variant ? <span className="change-tpl__variant"> · {selected.variant}</span> : null}
              </>
            ) : (
              'Pick a template above to continue.'
            )}
          </span>
          {/* Never disabled: leaving is always available, same as ✕. */}
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          {/* Says what happens next: this closes the modal and the re-flow
                carries on in the background. */}
          <button type="button" className="btn btn--primary" onClick={confirm} disabled={!selected || locked}>
            Change template
          </button>
        </div>
      </div>
    </Modal>
  );
}
